import {useContext} from 'react';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  Box,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {ThemeMode} from '../../enums/theme-mode';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Multiple select input field.
 */
export const MultipleSelectInput = (props: {
  label?: string;
  items: Array<{
    value: string;
    label: string;
  }>;
  handleChange: (value?: Array<string>) => void;
  placeholder?: string;
  value?: Array<string>;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  id?: string;
  className?: string;
  error?: string;
  handleBlur?: () => void;
}) => {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Get theme
  const theme = useTheme();

  // Change value
  const handleChange = (event: SelectChangeEvent<Array<string>>) => {
    props.handleChange(
      typeof event.target.value === 'string'
        ? [event.target.value]
        : event.target.value.length > 0
          ? event.target.value
          : undefined
    );
  };

  // Clear value
  const handleClear = () => {
    props.handleChange(undefined);
  };

  // Show clear button
  const showClearButton: boolean =
    props.value && props.value.length > 0 && props.clearable === true && props.disabled !== true
      ? true
      : false;

  return (
    <Box sx={{width: '100%'}} className={props.className}>
      <FormControl
        fullWidth
        variant="outlined"
        error={props.error ? true : false}
        sx={{
          // Fix border color when hovering on top of the clear button
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border:
              appContext.state.currentThemeMode === ThemeMode.Dark
                ? '1px solid white'
                : '1px solid black',
          },
        }}
      >
        <InputLabel
          disabled={props.disabled === true ? true : false}
          error={props.error ? true : false}
          color="neutral"
          shrink={true}
        >
          {props.required === true ? `${props.label || ''} *` : props.label || undefined}
        </InputLabel>
        <Select
          id={props.id}
          labelId={`${props.id}-multiple-select-label`}
          multiple={true}
          input={
            <OutlinedInput
              notched
              label={props.required === true ? `${props.label || ''} *` : props.label || undefined}
              error={props.error ? true : false}
            />
          }
          value={(props.value && props.value.length > 0 ? props.value : []) as any}
          displayEmpty={true}
          renderValue={(value) => {
            // Return placeholder or processed the value
            if (value && value.length > 0) {
              let processedValue: Array<string> = [];

              for (let i = 0; i < value.length; i++) {
                const item = props.items.find((item) => item.value === value[i]);
                if (item) {
                  processedValue.push(item.label);
                }
              }

              return processedValue.length > 0
                ? processedValue.join(', ')
                : props.placeholder || '';
            } else {
              return props.placeholder || '';
            }
          }}
          onChange={handleChange}
          onBlur={props.handleBlur}
          required={props.required === true ? true : false}
          disabled={props.disabled === true ? true : false}
          error={props.error ? true : false}
          color="neutral"
          sx={{
            '& .MuiSelect-select': {
              paddingRight: showClearButton ? '60px !important' : '32px',
            },
            width: '100%',
            color:
              props.value && props.value.length > 0
                ? theme.palette.text.primary
                : theme.palette.text.disabled,
          }}
        >
          {props.items.map((obj) => {
            return (
              <MenuItem
                key={`${props.id ? props.id : 'multiple-select-input'}-menu-item-${obj.value}`}
                value={obj.value}
              >
                {obj.label}
              </MenuItem>
            );
          })}
        </Select>
        {showClearButton && (
          <IconButton
            onClick={handleClear}
            sx={{
              position: 'absolute',
              right: '32px',
              top: '14px',
              width: '28px',
              height: '28px',
            }}
          >
            <ClearIcon sx={{fontSize: '20px'}} />
          </IconButton>
        )}
        {props.error && <FormHelperText>{props.error}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
